import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  AdoConnectionStatus,
  AdoOrganization,
  AdoProject,
  ArchitectureImportSummary,
  GeneratedWorkItem,
  GenerateBacklogResponse,
  ProjectAdoMapping,
  adoIntegrationService,
} from '../../services/adoIntegrationService';
import { projectService } from '../../services/projectService';
import { ProjectItem } from '../../types/project';
import { logger } from '../../utils/logger';

import AdoConnectionPanel from './AdoConnectionPanel';
import { buildArchieImportsLink } from './adoLinks';
import BacklogMonitoringPanel from './BacklogMonitoringPanel';
import BacklogTreeView from './BacklogTreeView';
import {
  importStatusBadge,
  importStatusLabel,
  NON_TERMINAL_IMPORT_STATUSES,
} from './importStatus';
import './azure-devops.css';

interface PageState {
  project: ProjectItem | null;
  status: AdoConnectionStatus | null;
  mapping: ProjectAdoMapping | null;
  organizations: AdoOrganization[];
  selectedOrgName: string;
  adoProjects: AdoProject[];
  selectedAdoProject: string;
  selectedAdoProjectId: string;
  loading: boolean;
  error: string | null;
}

const initialState: PageState = {
  project: null,
  status: null,
  mapping: null,
  organizations: [],
  selectedOrgName: '',
  adoProjects: [],
  selectedAdoProject: '',
  selectedAdoProjectId: '',
  loading: true,
  error: null,
};

/**
 * Slow LLM-backed calls (generate / send) routinely take 60-120 s.
 * Surface a live elapsed timer + reassuring copy so the user knows
 * the request hasn't stalled.
 */
const SLOW_CALL_HINT_AFTER_SECONDS = 20;

const ElapsedBadge: React.FC<{ label: string; hint: string }> = ({
  label,
  hint,
}) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = globalThis.setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => globalThis.clearInterval(id);
  }, []);
  const showHint = elapsed >= SLOW_CALL_HINT_AFTER_SECONDS;
  return (
    <div
      className="alert alert-info text-sm"
      role="status"
      aria-live="polite"
    >
      <span className="loading loading-spinner loading-sm" />
      <div className="flex flex-col">
        <span>
          <strong>{label}</strong>
          {' — '}
          {elapsed}s elapsed
        </span>
        {showHint && (
          <span className="text-xs opacity-80">{hint}</span>
        )}
      </div>
    </div>
  );
};

/**
 * Per-project "Send to Codie" workflow.
 *
 * Each Archie project is permanently linked to a single Azure DevOps
 * project via a persistent mapping. The link is created the first
 * time a user picks a target, and changing it requires an explicit
 * confirmation step to discourage accidental retargeting.
 */
const SendToAdoPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projectIdNum = projectId ? Number.parseInt(projectId, 10) : NaN;

  const [state, setState] = useState<PageState>(initialState);
  const [editingTarget, setEditingTarget] = useState(false);
  const [confirmAcknowledged, setConfirmAcknowledged] = useState(false);
  const [markdown, setMarkdown] = useState<string>('');
  const [showMarkdown, setShowMarkdown] = useState(false);
  const [extraInstructions, setExtraInstructions] = useState('');
  const [generation, setGeneration] = useState<GenerateBacklogResponse | null>(
    null,
  );
  const [imports, setImports] = useState<ArchitectureImportSummary[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const loadAll = React.useCallback(async () => {
    if (Number.isNaN(projectIdNum)) {
      setState((s) => ({ ...s, error: 'Invalid project id', loading: false }));
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const [project, status, mapping, projectImports] = await Promise.all([
        projectService.getProject(projectIdNum),
        adoIntegrationService.getConnectionStatus(),
        adoIntegrationService
          .getProjectAdoMapping(projectIdNum)
          .catch(() => null),
        adoIntegrationService
          .listProjectImports(projectIdNum)
          .catch(() => [] as ArchitectureImportSummary[]),
      ]);
      setImports(projectImports);
      setState((s) => ({
        ...s,
        project,
        status,
        mapping,
        // If no mapping yet, open the picker straight away.
        loading: false,
      }));
      setEditingTarget(mapping === null);
    } catch (err) {
      setState((s) => ({
        ...s,
        loading: false,
        error: (err as Error).message,
      }));
    }
  }, [projectIdNum]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Auto-load organizations once connected and the user is editing the target.
  useEffect(() => {
    if (
      state.status?.connected &&
      editingTarget &&
      state.organizations.length === 0 &&
      busy === null
    ) {
      handleLoadOrganizations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status?.connected, editingTarget]);

  // Auto-load ADO projects whenever the selected organization changes.
  useEffect(() => {
    if (state.selectedOrgName && state.adoProjects.length === 0) {
      void handleLoadAdoProjects(state.selectedOrgName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedOrgName]);

  const handleLoadOrganizations = async () => {
    setBusy('orgs');
    try {
      const orgs = await adoIntegrationService.listOrganizations();
      setState((s) => ({
        ...s,
        organizations: orgs,
        selectedOrgName: s.selectedOrgName || orgs[0]?.account_name || '',
      }));
    } catch (err) {
      setState((s) => ({ ...s, error: (err as Error).message }));
    } finally {
      setBusy(null);
    }
  };

  const handleLoadAdoProjects = async (orgName: string) => {
    if (!orgName) {return;}
    setBusy('projects');
    try {
      const projects = await adoIntegrationService.listProjects(orgName);
      setState((s) => ({
        ...s,
        selectedOrgName: orgName,
        adoProjects: projects,
      }));
    } catch (err) {
      setState((s) => ({ ...s, error: (err as Error).message }));
    } finally {
      setBusy(null);
    }
  };

  const handleStartChangeTarget = () => {
    setConfirmAcknowledged(false);
    setEditingTarget(true);
    setState((s) => ({
      ...s,
      organizations: [],
      adoProjects: [],
      selectedOrgName: '',
      selectedAdoProject: '',
      selectedAdoProjectId: '',
    }));
  };

  const handleCancelChangeTarget = () => {
    setEditingTarget(false);
    setConfirmAcknowledged(false);
  };

  const handleSaveTarget = async () => {
    if (!state.selectedOrgName || !state.selectedAdoProject) {return;}
    if (state.mapping !== null && !confirmAcknowledged) {return;}
    setBusy('save-mapping');
    try {
      const saved = await adoIntegrationService.setProjectAdoMapping(
        projectIdNum,
        {
          ado_org: state.selectedOrgName,
          ado_project: state.selectedAdoProject,
          ado_project_id: state.selectedAdoProjectId || null,
          confirm_change: state.mapping !== null,
        },
      );
      setState((s) => ({ ...s, mapping: saved, error: null }));
      setEditingTarget(false);
      setConfirmAcknowledged(false);
    } catch (err) {
      setState((s) => ({ ...s, error: extractCodieMessage(err) }));
    } finally {
      setBusy(null);
    }
  };

  const handlePreviewMarkdown = async () => {
    setBusy('markdown');
    try {
      const md = await adoIntegrationService.getProjectMarkdown(projectIdNum);
      setMarkdown(md);
      setShowMarkdown(true);
    } catch (err) {
      setState((s) => ({ ...s, error: (err as Error).message }));
    } finally {
      setBusy(null);
    }
  };

  const handleGenerate = async () => {
    setBusy('generate');
    try {
      const res = await adoIntegrationService.generateBacklog(projectIdNum, {
        additionalInstructions: extraInstructions || undefined,
        sourceMarkdownOverride: showMarkdown && markdown ? markdown : undefined,
      });
      setGeneration(res);
      if (res.source_markdown) {setMarkdown(res.source_markdown);}
    } catch (err) {
      setState((s) => ({ ...s, error: (err as Error).message }));
    } finally {
      setBusy(null);
    }
  };

  const handleSendToCodie = async () => {
    if (!generation || !state.project || !state.mapping) {return;}
    setBusy('send');
    try {
      const created = await adoIntegrationService.sendToCodie(projectIdNum, {
        title: state.project.title,
        summary: state.project.description ?? null,
        root_items: generation.root_items,
        analysis_model: generation.model_deployment ?? null,
        analysis_tokens_in: generation.tokens_in ?? null,
        analysis_tokens_out: generation.tokens_out ?? null,
      });
      setImports([created, ...imports]);
      // Clear the generated preview once it's been published so the
      // user isn't tempted to re-send the same tree; the new row in
      // the imports table is now the source of truth.
      setGeneration(null);
    } catch (err) {
      setState((s) => ({ ...s, error: extractCodieMessage(err) }));
    } finally {
      setBusy(null);
    }
  };

  const handleTreeChange = (items: GeneratedWorkItem[]) => {
    if (!generation) {return;}
    setGeneration({ ...generation, root_items: items });
  };

  const handleRefreshImport = React.useCallback(
    async (importId: number) => {
      try {
        const refreshed = await adoIntegrationService
          .refreshImportStatus(importId);
        setImports((prev) =>
          prev.map((row) => (row.id === refreshed.id ? refreshed : row)),
        );
      } catch (err) {
        logger.warn('refreshImportStatus failed', err);
      }
    },
    [],
  );

  const canSave = useMemo(() => {
    const hasConnection = Boolean(state.status?.connected);
    const hasTarget = Boolean(
      state.selectedOrgName && state.selectedAdoProject,
    );
    const mappingOk = state.mapping === null || confirmAcknowledged;
    return hasConnection && hasTarget && mappingOk;
  },
  [
    state.status?.connected,
    state.selectedOrgName,
    state.selectedAdoProject,
    state.mapping,
    confirmAcknowledged,
  ],
  );

  const canSend = useMemo(
    () => Boolean(state.status?.connected && state.mapping && generation),
    [state.status?.connected, state.mapping, generation],
  );

  if (state.loading) {
    return <div className="p-6">Loading…</div>;
  }
  if (state.error) {
    return (
      <div className="p-6">
        <div className="alert alert-error">{state.error}</div>
        <button className="btn mt-4" onClick={() => { loadAll(); }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-onSurface-strong">
            Send “{state.project?.title}” to Codie
          </h1>
          <p className="text-text-onSurface-subtle mt-1">
            Generate a backlog from the project's architecture and publish
            it to Azure DevOps via the Codie ADO plugin.
          </p>
        </div>
        <Link
          to="/azure-devops"
          className="px-3 py-1.5 rounded text-sm font-medium border border-border-medium bg-background-surface hover:bg-background-section text-text-onSurface-strong transition"
        >
          ← Hub
        </Link>
      </header>

      <ProgressTracker
        steps={[
          { label: 'Connect', done: Boolean(state.status?.connected) },
          { label: 'Link ADO project', done: Boolean(state.mapping) },
          { label: 'Generate', done: Boolean(generation) },
          { label: 'Send to Codie', done: imports.length > 0 },
        ]}
      />

      <AdoConnectionPanel
        status={state.status}
        loading={false}
        onChange={() => { loadAll(); }}
        returnTo={`/project/${projectIdNum}/azure-devops`}
      />

      {state.status?.connected && (
        <TargetMappingCard
          mapping={state.mapping}
          editing={editingTarget}
          confirmAcknowledged={confirmAcknowledged}
          onAcknowledgeChange={setConfirmAcknowledged}
          organizations={state.organizations}
          adoProjects={state.adoProjects}
          selectedOrgName={state.selectedOrgName}
          selectedAdoProject={state.selectedAdoProject}
          busy={busy}
          canSave={canSave}
          onStartChange={handleStartChangeTarget}
          onCancelChange={handleCancelChangeTarget}
          onSelectOrg={(org) =>
            setState((s) => ({
              ...s,
              selectedOrgName: org,
              adoProjects: [],
              selectedAdoProject: '',
              selectedAdoProjectId: '',
            }))
          }
          onSelectProject={(name, id) =>
            setState((s) => ({
              ...s,
              selectedAdoProject: name,
              selectedAdoProjectId: id,
            }))
          }
          onReloadOrgs={() => void handleLoadOrganizations()}
          onReloadProjects={() =>
            void handleLoadAdoProjects(state.selectedOrgName)
          }
          onSave={() => void handleSaveTarget()}
        />
      )}

      {state.status?.connected && state.mapping && (
        <BacklogMonitoringPanel
          projectId={projectIdNum}
          mapping={state.mapping}
        />
      )}

      <div className="card bg-base-100 border border-base-300 shadow-sm">
        <div className="card-body gap-3">
          <h3 className="card-title text-lg">Generate backlog</h3>
          <textarea
            className="textarea textarea-bordered w-full"
            placeholder="Optional extra instructions for codie-api…"
            value={extraInstructions}
            onChange={(e) => setExtraInstructions(e.target.value)}
            rows={2}
          />
          <div className="card-actions justify-end">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => void handlePreviewMarkdown()}
              disabled={busy !== null}
            >
              {busy === 'markdown' ? 'Loading…' : 'Preview prompt markdown'}
            </button>
            <button
              type="button"
              className="btn btn-primary main-button"
              onClick={() => void handleGenerate()}
              disabled={busy !== null}
            >
              {busy === 'generate' ? (
                <>
                  <span className="loading loading-spinner loading-sm" />
                  Generating…
                </>
              ) : (
                'Generate backlog'
              )}
            </button>
          </div>
          {busy === 'generate' && (
            <ElapsedBadge
              label="Generating backlog preview"
              hint="Codie is calling the LLM to draft the backlog. This usually takes 60–120 seconds for larger architectures — please keep the tab open."
            />
          )}
          {showMarkdown && (
            <details className="bg-base-100 rounded p-3" open>
              <summary className="cursor-pointer font-medium">
                Source markdown ({markdown.length} chars)
              </summary>
              <textarea
                className="textarea textarea-bordered w-full mt-2 font-mono text-xs"
                rows={10}
                value={markdown}
                onChange={(e) => setMarkdown(e.target.value)}
              />
            </details>
          )}
        </div>
      </div>

      {generation && (
        <div className="card bg-base-100 border border-base-300 shadow-sm">
          <div className="card-body gap-3">
            <h3 className="card-title text-lg">Review backlog tree</h3>
            <p className="text-sm opacity-70">
              {generation.root_items.length} top-level item(s) generated
              {generation.model_deployment
                ? ` by ${generation.model_deployment}`
                : ''}
              . Edit titles inline before sending — your changes are
              what gets imported into Azure DevOps.
            </p>
            <BacklogTreeView
              items={generation.root_items}
              onChange={handleTreeChange}
            />
            <div className="card-actions justify-end">
              <button
                type="button"
                className="btn btn-primary main-button"
                onClick={() => void handleSendToCodie()}
                disabled={!canSend || busy !== null}
              >
                {busy === 'send' ? (
                  <>
                    <span className="loading loading-spinner loading-sm" />
                    Sending…
                  </>
                ) : (
                  'Send to Codie'
                )}
              </button>
            </div>
            {busy === 'send' && (
              <ElapsedBadge
                label="Sending architecture to Codie"
                hint="Codie is persisting your reviewed backlog as-is — no regeneration. This usually takes a couple of seconds."
              />
            )}
            {!canSend && (
              <p className="text-xs opacity-60 text-right">
                Link this project to an Azure DevOps project above to enable
                Send.
              </p>
            )}
          </div>
        </div>
      )}

      <section className="card bg-base-100 border border-base-300 shadow-sm">
        <div className="card-body gap-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="card-title text-lg">Imports</h2>
            {state.mapping && (
              <a
                href={buildArchieImportsLink(
                  state.mapping.ado_org,
                  state.mapping.ado_project,
                )}
                target="_blank"
                rel="noreferrer noopener"
                className="link link-primary text-xs shrink-0"
              >
                Open Archie Imports in ADO ↗
              </a>
            )}
          </div>
          {imports.length === 0 ? (
            <p className="opacity-60">
              No architectures sent to Codie for this project yet.
            </p>
          ) : (
            <ul className="space-y-2">
              {imports.map((imp) => (
                <li
                  key={imp.id}
                  className="bg-base-200 rounded px-3 py-2 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">{imp.title}</div>
                    <div className="text-xs opacity-70">
                      {imp.ado_org}/{imp.ado_project} ·{' '}
                      {new Date(imp.submitted_at).toLocaleString()} ·{' '}
                      {imp.work_item_count} items · Sent by {imp.created_by}
                    </div>
                    <div className="text-xs opacity-50 font-mono">
                      {imp.codie_import_id}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`badge ${importStatusBadge(imp.status)}`}
                    >
                      {importStatusLabel(imp.status)}
                    </span>
                    {NON_TERMINAL_IMPORT_STATUSES.has(imp.status) && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        title="Check Codie for the latest status"
                        onClick={() => { handleRefreshImport(imp.id); }}
                      >
                        Refresh
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => navigate(`/project/${projectIdNum}/setup`)}
      >
        ← Back to project
      </button>
    </div>
  );
};

/**
 * Best-effort extraction of a friendly message from a structured codie
 * error envelope (AR-4). Falls back to the raw error message.
 */
function extractCodieMessage(err: unknown): string {
  const detail = (err as { data?: { detail?: { message?: string } } })?.data
    ?.detail;
  if (detail && typeof detail.message === 'string') {
    return detail.message;
  }
  return (err as Error).message;
}

interface TargetMappingCardProps {
  mapping: ProjectAdoMapping | null;
  editing: boolean;
  confirmAcknowledged: boolean;
  onAcknowledgeChange: (value: boolean) => void;
  organizations: AdoOrganization[];
  adoProjects: AdoProject[];
  selectedOrgName: string;
  selectedAdoProject: string;
  busy: string | null;
  canSave: boolean;
  onStartChange: () => void;
  onCancelChange: () => void;
  onSelectOrg: (org: string) => void;
  onSelectProject: (name: string, id: string) => void;
  onReloadOrgs: () => void;
  onReloadProjects: () => void;
  onSave: () => void;
}

/**
 * Card that either displays the persistent ADO target for the project
 * (with a guarded Change button) or the picker UI to create / change it.
 */
const TargetMappingCard: React.FC<TargetMappingCardProps> = ({
  mapping,
  editing,
  confirmAcknowledged,
  onAcknowledgeChange,
  organizations,
  adoProjects,
  selectedOrgName,
  selectedAdoProject,
  busy,
  canSave,
  onStartChange,
  onCancelChange,
  onSelectOrg,
  onSelectProject,
  onReloadOrgs,
  onReloadProjects,
  onSave,
}) => {
  // Locked view — mapping exists and we are not editing.
  if (mapping !== null && !editing) {
    return (
      <div className="card bg-base-100 border border-base-300 shadow-sm">
        <div className="card-body gap-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="card-title text-lg">Linked Azure DevOps project</h3>
              <p className="text-xs text-text-onSurface-subtle">
                Each Archie project is permanently linked to one Azure DevOps
                project. Changing the link does not move existing imports.
              </p>
            </div>
            <button
              type="button"
              className="px-3 py-1.5 rounded text-sm font-medium border border-border-medium bg-background-surface hover:bg-background-section text-text-onSurface-strong transition"
              onClick={onStartChange}
            >
              Change target
            </button>
          </div>
          <div className="rounded-md bg-background-section px-4 py-3">
            <div className="text-lg font-semibold text-text-onSurface-strong">
              {mapping.ado_org} / {mapping.ado_project}
            </div>
            <div className="text-xs text-text-onSurface-subtle mt-1">
              Linked by {mapping.set_by} on{' '}
              {new Date(mapping.updated_at).toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Picker / change view.
  const isChange = mapping !== null;
  return (
    <div className="card bg-base-100 border border-base-300 shadow-sm">
      <div className="card-body gap-4">
        <h3 className="card-title text-lg">
          {isChange ? 'Change Azure DevOps target' : 'Link Azure DevOps project'}
        </h3>
        <p className="text-xs text-text-onSurface-subtle">
          Codie's plugin will surface every future import in the chosen
          project's 📥 Archie Imports tab. Pick the target carefully — the
          link is meant to stay put.
        </p>
        {isChange && (
          <div className="alert bg-system-subtle-warning text-text-onSurface-strong text-sm">
            <span>
              You are about to retarget this Archie project away from{' '}
              <strong>
                {mapping?.ado_org}/{mapping?.ado_project}
              </strong>
              . Existing imports stay attributed to the old target.
            </span>
          </div>
        )}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="form-control">
            <label className="label" htmlFor="ado-org-select">
              <span className="label-text font-medium">Organization</span>
            </label>
            <div className="join w-full">
              <select
                id="ado-org-select"
                className="select select-bordered join-item flex-1"
                value={selectedOrgName}
                onChange={(e) => onSelectOrg(e.target.value)}
                disabled={busy === 'orgs'}
              >
                <option value="">
                  {busy === 'orgs'
                    ? 'Loading organizations…'
                    : 'Select organization…'}
                </option>
                {organizations.map((o) => (
                  <option key={o.account_id} value={o.account_name}>
                    {o.account_name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-ghost join-item"
                onClick={onReloadOrgs}
                disabled={busy !== null}
                title="Reload organizations"
              >
                {busy === 'orgs' ? '…' : '↻'}
              </button>
            </div>
          </div>
          <div className="form-control">
            <label className="label" htmlFor="ado-project-select">
              <span className="label-text font-medium">Project</span>
            </label>
            <div className="join w-full">
              <select
                id="ado-project-select"
                className="select select-bordered join-item flex-1"
                value={selectedAdoProject}
                onChange={(e) => {
                  const opt = adoProjects.find(
                    (p) => p.name === e.target.value,
                  );
                  onSelectProject(e.target.value, opt?.id ?? '');
                }}
                disabled={!selectedOrgName || busy === 'projects'}
              >
                <option value="">
                  {!selectedOrgName
                    ? 'Select an organization first'
                    : busy === 'projects'
                      ? 'Loading projects…'
                      : 'Select project…'}
                </option>
                {adoProjects.map((p) => (
                  <option key={p.id} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-ghost join-item"
                onClick={onReloadProjects}
                disabled={!selectedOrgName || busy !== null}
                title="Reload projects"
              >
                {busy === 'projects' ? '…' : '↻'}
              </button>
            </div>
          </div>
        </div>
        {isChange && (
          <label className="label cursor-pointer justify-start gap-3">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={confirmAcknowledged}
              onChange={(e) => onAcknowledgeChange(e.target.checked)}
            />
            <span className="label-text text-sm">
              I understand this changes where new imports land for this
              Archie project.
            </span>
          </label>
        )}
        <div className="card-actions justify-end">
          {isChange && (
            <button
              type="button"
              className="px-3 py-1.5 rounded text-sm font-medium border border-border-medium bg-background-surface hover:bg-background-section text-text-onSurface-strong transition"
              onClick={onCancelChange}
              disabled={busy !== null}
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary main-button"
            onClick={onSave}
            disabled={!canSave || busy !== null}
          >
            {busy === 'save-mapping'
              ? 'Saving…'
              : isChange
                ? 'Confirm change'
                : 'Link this project'}
          </button>
        </div>
      </div>
    </div>
  );
};

interface ProgressTrackerProps {
  steps: { label: string; done: boolean }[];
}

/**
 * Horizontal step indicator matched to Archie's brand styling:
 * Shell-yellow filled bar over a soft gray track, with numbered
 * markers. Mirrors the progress bar used on the brownfield flow.
 *
 * Steps are treated as cumulative: if a later step is marked done
 * (e.g. an import already exists), every earlier step is filled in
 * too. This avoids the confusing case where a user returns to the
 * page after sending and only the last step lights up.
 */
const ProgressTracker: React.FC<ProgressTrackerProps> = ({ steps }) => {
  const lastDoneIdx = steps.reduce(
    (acc, step, idx) => (step.done ? idx : acc),
    -1,
  );
  const effectiveSteps = steps.map((step, idx) => ({
    ...step,
    done: idx <= lastDoneIdx,
  }));
  const completed = effectiveSteps.filter((s) => s.done).length;
  const pct = Math.round((completed / effectiveSteps.length) * 100);
  return (
    <div className="rounded-lg border border-border-medium bg-background-surface shadow-sm px-5 py-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-text-onSurface-subtle">
          Workflow
        </span>
        <span className="text-xs text-text-onSurface-subtle">
          {completed} of {effectiveSteps.length} complete
        </span>
      </div>
      <div className="h-2 bg-background-base rounded-full overflow-hidden">
        <div
          className="h-full bg-[rgb(255,198,0)] transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <ol className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {effectiveSteps.map((step, idx) => (
          <li
            key={step.label}
            className={`flex items-center gap-2 text-sm ${
              step.done
                ? 'text-text-onSurface-strong'
                : 'text-text-onSurface-subtle'
            }`}
          >
            <span
              className={`h-6 w-6 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold border ${
                step.done
                  ? 'bg-[rgb(255,198,0)] border-[rgb(255,198,0)] text-text-onSurface-strong'
                  : 'bg-background-surface border-border-medium text-text-onSurface-subtle'
              }`}
            >
              {step.done ? '✓' : idx + 1}
            </span>
            <span className="truncate font-medium">{step.label}</span>
          </li>
        ))}
      </ol>
    </div>
  );
};

export default SendToAdoPage;
